import { useMemo } from 'react';

import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import InsertChartOutlinedSharpIcon from '@mui/icons-material/InsertChartOutlinedSharp';
import MapIcon from '@mui/icons-material/Map';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import { Box, CircularProgress, styled } from '@mui/material';

import {
  List,
  ListItemNavLink,
  StyledHelpListItemIconWrapper,
  Bar,
  AbsoluteLink,
} from './LeftNavbarV2.styles';

import { ReactComponent as DocIcon } from '../../assets/images/doc.svg';
import {
  TRANSACTIONS_ROUTE,
  ANALYTICS_ROUTE,
  PROVIDER_TRANSACTIONS_ROUTE,
  PROVIDER_LOCATIONS_ROUTE,
  PROVIDER_DASHBOARD_ROUTE,
  PROVIDER_PROFILE_ROUTE,
} from '../../config/routes';
import { isV2PermissionsEnabled } from '../../helpers/featureFlags/isV2PermissionsEnabled';
import { getSessionTeamType } from '../../helpers/roles/getTeamType';
import { getLocationRoute } from '../../helpers/routes/getLocationRoute';
import { getAbsLinkWithCurrentOrigin } from '../../utils/path/getAbsLinkWithCurrentOrigin';
import { useUserInfo } from '../../utils/permissions';
import { skipNextUserLogInEvent } from '../GtmUserTrack/GtmUserTrack';
import { NavHelpMenuV2 } from '../NavHelpMenuV2/NavHelpMenuV2';

const CircularProgressBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10,
});

export const LeftNavbarV2 = () => {
  const mapRoute = getLocationRoute();
  const brokerItems = useMemo(
    () => [
      {
        label: 'Map',
        icon: <MapIcon />,
        to: mapRoute,
      },
      {
        label: 'Transactions',
        icon: <PieChartOutlineIcon />,
        to: TRANSACTIONS_ROUTE,
      },
      {
        label: 'Analytics',
        icon: <InsertChartOutlinedSharpIcon />,
        to: ANALYTICS_ROUTE,
      },
      {
        label: 'My Surveys',
        icon: <DocIcon />,
        to: getAbsLinkWithCurrentOrigin('/fast-dashboard/deals'),
        absolute: true,
        onClick: skipNextUserLogInEvent,
      },
    ],
    [mapRoute],
  );

  const inventoryAnalystItems = useMemo(
    () => [
      {
        label: 'Transactions',
        icon: <PieChartOutlineIcon />,
        to: PROVIDER_TRANSACTIONS_ROUTE,
      },
      {
        label: 'Locations',
        icon: <FmdGoodOutlinedIcon />,
        to: PROVIDER_LOCATIONS_ROUTE,
      },
    ],
    [],
  );

  const providerItems = useMemo(
    () => [
      {
        label: 'Transactions',
        icon: <PieChartOutlineIcon />,
        to: PROVIDER_TRANSACTIONS_ROUTE,
      },
      {
        label: 'Locations',
        icon: <FmdGoodOutlinedIcon />,
        to: PROVIDER_LOCATIONS_ROUTE,
      },

      {
        label: 'Inquiries',
        icon: <FactCheckOutlinedIcon />,
        to: PROVIDER_DASHBOARD_ROUTE,
      },
      {
        label: 'Provider Profile',
        icon: <FolderSharedOutlinedIcon />,
        to: PROVIDER_PROFILE_ROUTE,
      },
    ],
    [],
  );

  const items = useMemo(() => {
    const teamType = getSessionTeamType();

    switch (teamType) {
      case 'Broker':
      case 'Flexpert Support':
        return brokerItems;
      case 'Provider':
        return providerItems;
      case 'Inventory Analyst':
        return inventoryAnalystItems;
      default:
        return [];
    }
  }, [brokerItems, inventoryAnalystItems, providerItems]);

  const ALL_NAV_ITEMS = {
    Map: {
      label: 'Map',
      icon: <MapIcon />,
      to: getLocationRoute(),
    },
    Transactions: {
      label: 'Transactions',
      icon: <PieChartOutlineIcon />,
      to: TRANSACTIONS_ROUTE,
    },
    Analytics: {
      label: 'Analytics',
      icon: <InsertChartOutlinedSharpIcon />,
      to: ANALYTICS_ROUTE,
    },
    MySurveys: {
      label: 'My Surveys',
      icon: <DocIcon />,
      to: getAbsLinkWithCurrentOrigin('/fast-dashboard/deals'),
      absolute: true,
      onClick: skipNextUserLogInEvent,
    },
    ProviderTransactions: {
      label: 'Transactions',
      icon: <PieChartOutlineIcon />,
      to: PROVIDER_TRANSACTIONS_ROUTE,
    },
    Locations: {
      label: 'Locations',
      icon: <FmdGoodOutlinedIcon />,
      to: PROVIDER_LOCATIONS_ROUTE,
    },
    Inquiries: {
      label: 'Inquiries',
      icon: <FactCheckOutlinedIcon />,
      to: PROVIDER_DASHBOARD_ROUTE,
    },
    ProviderProfile: {
      label: 'Provider Profile',
      icon: <FolderSharedOutlinedIcon />,
      to: PROVIDER_PROFILE_ROUTE,
    },
  };

  const ROLE_NAV_KEYS = {
    FAST_MK2_SUPER_ADMIN: ['Map', 'Transactions', 'Analytics', 'MySurveys', 'Locations'],
    FAST_MK2_ADMIN: ['Map', 'Transactions', 'Analytics', 'MySurveys', 'Locations'],
    FAST_MK2_REGIONAL_LEAD: ['Map', 'Transactions', 'Analytics', 'MySurveys', 'Locations'],
    FAST_MK2_ADVANCED_FLEXPERT_SUPPORT: [
      'Map',
      'Transactions',
      'Analytics',
      'MySurveys',
      'Locations',
    ],
    FAST_MK2_FLEXPERT: ['Map', 'Transactions', 'Analytics', 'MySurveys', 'Locations'],
    FAST_MK2_FLEXPERT_SUPPORT: ['Map', 'Transactions', 'Analytics', 'MySurveys', 'Locations'],
    FAST_MK2_BROKER: ['Map', 'Transactions', 'Analytics', 'MySurveys', 'Locations'],
    FAST_MK2_TRANSACTION_MANAGER: ['Map', 'Transactions', 'Analytics', 'MySurveys'],
    FAST_MK2_CLIENT: ['Map', 'Transactions', 'Analytics'],
    FAST_MK2_BASIC_PROVIDER: ['ProviderTransactions', 'Locations', 'Inquiries', 'ProviderProfile'],
    FAST_MK2_ADVANCED_PROVIDER: [
      'Map',
      'ProviderTransactions',
      'Locations',
      'Inquiries',
      'ProviderProfile',
    ],
  };

  const { userInfo, userInfoLoading } = useUserInfo();

  const itemsV2 = useMemo(() => {
    if (!userInfo) return [];

    const roleCodes = userInfo?.roleAssociations?.map((r) => r.code) || [];

    const uniqueNavKeys = new Set();

    roleCodes.forEach((code) => {
      const keys = ROLE_NAV_KEYS[code];

      if (keys) {
        keys.forEach((key) => uniqueNavKeys.add(key));
      }
    });

    return Array.from(uniqueNavKeys)
      .map((key) => ALL_NAV_ITEMS[key])
      .filter(Boolean);
  }, [userInfo, mapRoute]);

  const results = isV2PermissionsEnabled() ? itemsV2 : items;

  return (
    <Bar>
      <List>
        {userInfoLoading ? (
          <CircularProgressBox>
            <CircularProgress />
          </CircularProgressBox>
        ) : (
          results.map((item) => (
            <li key={item.label} title={item.label}>
              {item.absolute ? (
                <AbsoluteLink href={item.to} onClick={item.onClick}>
                  {item.icon}
                  {item.label}
                </AbsoluteLink>
              ) : (
                <ListItemNavLink to={item.to}>
                  {item.icon}
                  {item.label}
                </ListItemNavLink>
              )}
            </li>
          ))
        )}
        <StyledHelpListItemIconWrapper>
          <NavHelpMenuV2 />
        </StyledHelpListItemIconWrapper>
      </List>
    </Bar>
  );
};
